// CustomerModal Component - Phone lookup with 2FA SMS verification
import React, { useState, useRef, useEffect } from 'react';
import { api } from '../api/client';

export function CustomerModal({ 
  open, 
  venueid, 
  onClose, 
  onCustomerFound, 
  onGuestCheckout,
  settings 
}) {
  const [step, setStep] = useState('phone'); // phone, verify, found, guest
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [foundCustomer, setFoundCustomer] = useState(null);
  
  // 2FA verification state
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [resendTimer, setResendTimer] = useState(0);
  const codeInputRefs = useRef([]);
  
  // Guest form state
  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestEmail, setGuestEmail] = useState('');

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Reset form state when modal opens
  useEffect(() => {
    if (open) {
      setStep('phone');
      setPhone('');
      setError('');
      setFoundCustomer(null);
      setVerificationCode(['', '', '', '', '', '']);
      setGuestFirstName('');
      setGuestLastName('');
      setGuestPhone('');
      setGuestEmail('');
      setResendTimer(0);
      setLoading(false);
    }
  }, [open]);

  // Focus first code input when entering verify step
  useEffect(() => {
    if (step === 'verify' && codeInputRefs.current[0]) {
      codeInputRefs.current[0].focus();
    }
  }, [step]);

  if (!open) return null;

  // Format phone number for display
  const formatPhoneDisplay = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0,3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6,10)}`;
  };

  // Handle sending verification code
  const handleSendCode = async () => {
    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.requestVerificationCode(cleanPhone, venueid);
      
      if (response.result === 1) {
        setStep('verify');
        setResendTimer(60); // 60 second cooldown
        setVerificationCode(['', '', '', '', '', '']);
      } else {
        setError(response.message || 'Failed to send verification code');
      }
    } catch (err) {
      setError('Failed to send code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle code input
  const handleCodeInput = (index, value) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);
    
    const newCode = [...verificationCode];
    newCode[index] = digit;
    setVerificationCode(newCode);
    
    // Auto-advance to next input
    if (digit && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit when all digits entered
    if (digit && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        handleVerifyCode(fullCode);
      }
    }
  };

  // Handle backspace in code inputs
  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste in code inputs
  const handleCodePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length > 0) {
      const newCode = [...verificationCode];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newCode[i] = pastedData[i];
      }
      setVerificationCode(newCode);
      
      // Focus appropriate input
      const nextIndex = Math.min(pastedData.length, 5);
      codeInputRefs.current[nextIndex]?.focus();
      
      // Auto-submit if complete
      if (pastedData.length === 6) {
        handleVerifyCode(pastedData);
      }
    }
  };

  // Handle code verification
  const handleVerifyCode = async (code) => {
    const fullCode = code || verificationCode.join('');
    if (fullCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const cleanPhone = phone.replace(/\D/g, '');
      const response = await api.verifyCode(cleanPhone, fullCode, venueid);
      
      if (response.result === 1) {
        // Store JWT token
        if (response.token) {
          localStorage.setItem('stove_online_token', response.token);
        }
        
        // Customer found and verified
        if (response.customer) {
          setFoundCustomer(response.customer);
          onCustomerFound(response.customer);
        } else {
          // New customer - go to guest form to complete profile
          setGuestPhone(phone);
          setStep('guest');
        }
      } else {
        setError(response.message || 'Invalid verification code');
        setVerificationCode(['', '', '', '', '', '']);
        codeInputRefs.current[0]?.focus();
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
      setVerificationCode(['', '', '', '', '', '']);
    } finally {
      setLoading(false);
    }
  };

  // Handle resend code
  const handleResendCode = async () => {
    if (resendTimer > 0) return;
    
    setLoading(true);
    setError('');

    try {
      const cleanPhone = phone.replace(/\D/g, '');
      const response = await api.requestVerificationCode(cleanPhone, venueid);
      
      if (response.result === 1) {
        setResendTimer(60);
        setVerificationCode(['', '', '', '', '', '']);
        codeInputRefs.current[0]?.focus();
      } else {
        setError(response.message || 'Failed to resend code');
      }
    } catch (err) {
      setError('Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  // Handle guest checkout (skip 2FA)
  const handleGuestSubmit = async () => {
    if (!guestFirstName.trim()) {
      setError('Please enter your first name');
      return;
    }
    if (!guestLastName.trim()) {
      setError('Please enter your last name');
      return;
    }
    if (guestPhone.replace(/\D/g, '').length < 10) {
      setError('Please enter a valid phone number');
      return;
    }
    // Email is required
    if (!guestEmail.trim()) {
      setError('Please enter your email address');
      return;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(guestEmail.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create new customer record
      const firstName = guestFirstName.trim();
      const lastName = guestLastName.trim();
      
      const response = await api.updateCustomer({
        customerID: 0,
        venueid,
        first_name: firstName,
        last_name: lastName,
        mobile_phone: guestPhone.replace(/\D/g, ''),
        email: guestEmail
      });

      const customerData = {
        customerID: response.customerID || 0,
        first_name: firstName,
        last_name: lastName,
        mobile_phone: guestPhone,
        email: guestEmail,
        phone: guestPhone
      };

      if (response.result === '1' || response.result === 1) {
        customerData.customerID = response.customerID;
      }
      
      setLoading(false);
      onGuestCheckout(customerData);
    } catch (err) {
      // Allow guest checkout even if record creation fails
      setLoading(false);
      onGuestCheckout({
        customerID: 0,
        first_name: guestFirstName.trim(),
        last_name: guestLastName.trim(),
        mobile_phone: guestPhone,
        email: guestEmail,
        phone: guestPhone
      });
    }
  };

  const handleClose = () => {
    setStep('phone');
    setPhone('');
    setError('');
    setFoundCustomer(null);
    setVerificationCode(['', '', '', '', '', '']);
    setGuestFirstName('');
    setGuestLastName('');
    setGuestPhone('');
    setGuestEmail('');
    setResendTimer(0);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal customer-modal" onClick={e => e.stopPropagation()}>
        <button className="modal__close" onClick={handleClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {/* Phone Entry Step */}
        {step === 'phone' && (
          <div className="customer-modal__content">
            <div className="customer-modal__header">
              <h2 className="customer-modal__title">Welcome!</h2>
              <p className="customer-modal__subtitle">
                Enter your phone number and we'll send you a verification code
              </p>
            </div>

            <div className="customer-modal__form">
              <div className="customer-modal__input-group">
                <label className="customer-modal__label">Phone Number</label>
                <input
                  type="tel"
                  className="customer-modal__input customer-modal__input--large"
                  placeholder="(555) 123-4567"
                  value={formatPhoneDisplay(phone)}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendCode()}
                  autoFocus
                />
              </div>

              {error && (
                <p className="customer-modal__error">{error}</p>
              )}

              <button 
                className="customer-modal__btn customer-modal__btn--primary"
                onClick={handleSendCode}
                disabled={loading || phone.replace(/\D/g, '').length < 10}
              >
                {loading ? 'Sending...' : 'Send Verification Code'}
              </button>

              <button 
                className="customer-modal__btn customer-modal__btn--secondary"
                onClick={() => {
                  setStep('guest');
                }}
              >
                Continue as Guest
              </button>
            </div>
          </div>
        )}

        {/* Code Verification Step */}
        {step === 'verify' && (
          <div className="customer-modal__content">
            <div className="customer-modal__header">
              <h2 className="customer-modal__title">Enter Code</h2>
              <p className="customer-modal__subtitle">
                We sent a 6-digit code to<br/>
                <strong>{formatPhoneDisplay(phone)}</strong>
              </p>
            </div>

            <div className="customer-modal__form">
              <div className="customer-modal__code-inputs">
                {verificationCode.map((digit, index) => (
                  <input
                    key={index}
                    ref={el => codeInputRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    className="customer-modal__code-input"
                    value={digit}
                    onChange={(e) => handleCodeInput(index, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(index, e)}
                    onPaste={index === 0 ? handleCodePaste : undefined}
                  />
                ))}
              </div>

              {error && (
                <p className="customer-modal__error">{error}</p>
              )}

              <button 
                className="customer-modal__btn customer-modal__btn--primary"
                onClick={() => handleVerifyCode()}
                disabled={loading || verificationCode.join('').length !== 6}
              >
                {loading ? 'Verifying...' : 'Verify Code'}
              </button>

              <div className="customer-modal__resend">
                {resendTimer > 0 ? (
                  <p className="customer-modal__resend-timer">
                    Resend code in {resendTimer}s
                  </p>
                ) : (
                  <button 
                    className="customer-modal__btn customer-modal__btn--link"
                    onClick={handleResendCode}
                    disabled={loading}
                  >
                    Resend Code
                  </button>
                )}
              </div>

              <button 
                className="customer-modal__btn customer-modal__btn--link"
                onClick={() => {
                  setStep('phone');
                  setVerificationCode(['', '', '', '', '', '']);
                  setError('');
                }}
              >
                ← Change Phone Number
              </button>
            </div>
          </div>
        )}

        {/* Customer Found Step */}
        {step === 'found' && foundCustomer && (
          <div className="customer-modal__content">
            <div className="customer-modal__header">
              <h2 className="customer-modal__title">Welcome Back!</h2>
              <p className="customer-modal__subtitle">
                You're signed in
              </p>
            </div>

            <div className="customer-modal__found">
              <div className="customer-modal__found-info">
                <p className="customer-modal__found-name">
                  {foundCustomer.first_name} {foundCustomer.last_name}
                </p>
                <p className="customer-modal__found-phone">
                  {formatPhoneDisplay(foundCustomer.mobile_phone)}
                </p>
                {foundCustomer.email && (
                  <p className="customer-modal__found-email">
                    {foundCustomer.email}
                  </p>
                )}
                {foundCustomer.loyalty && (
                  <div className="customer-modal__loyalty">
                    <span className="customer-modal__loyalty-tier">
                      {foundCustomer.loyalty.tier || 'Member'}
                    </span>
                    <span className="customer-modal__loyalty-points">
                      {foundCustomer.loyalty.points || 0} points
                    </span>
                  </div>
                )}
              </div>

              <button 
                className="customer-modal__btn customer-modal__btn--primary"
                onClick={() => onCustomerFound(foundCustomer)}
              >
                Continue to Order
              </button>
            </div>
          </div>
        )}

        {/* Guest Checkout Step */}
        {step === 'guest' && (
          <div className="customer-modal__content">
            <div className="customer-modal__header">
              <h2 className="customer-modal__title">Your Details</h2>
              <p className="customer-modal__subtitle">
                We'll use this info to send you order updates
              </p>
            </div>

            <div className="customer-modal__form">
              <div className="customer-modal__input-group">
                <label className="customer-modal__label">First Name *</label>
                <input
                  type="text"
                  className="customer-modal__input"
                  placeholder="First name"
                  autoComplete="given-name"
                  value={guestFirstName}
                  onChange={(e) => setGuestFirstName(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="customer-modal__input-group">
                <label className="customer-modal__label">Last Name *</label>
                <input
                  type="text"
                  className="customer-modal__input"
                  placeholder="Last name"
                  autoComplete="family-name"
                  value={guestLastName}
                  onChange={(e) => setGuestLastName(e.target.value)}
                />
              </div>

              <div className="customer-modal__input-group">
                <label className="customer-modal__label">Phone *</label>
                <input
                  type="tel"
                  className="customer-modal__input"
                  placeholder="(555) 123-4567"
                  value={formatPhoneDisplay(guestPhone)}
                  onChange={(e) => setGuestPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                />
              </div>

              <div className="customer-modal__input-group">
                <label className="customer-modal__label">Email *</label>
                <input
                  type="email"
                  className="customer-modal__input"
                  placeholder="your@email.com"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  required
                />
              </div>

              {error && (
                <p className="customer-modal__error">{error}</p>
              )}

              <button 
                className="customer-modal__btn customer-modal__btn--primary"
                onClick={handleGuestSubmit}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Continue to Checkout'}
              </button>

              <button 
                className="customer-modal__btn customer-modal__btn--link"
                onClick={() => setStep('phone')}
              >
                ← Back
              </button>
            </div>
          </div>
        )}

        <style>{`
          .customer-modal__code-inputs {
            display: flex;
            justify-content: center;
            gap: 0.5rem;
            margin-bottom: 1.5rem;
          }
          
          .customer-modal__code-input {
            width: 3rem;
            height: 3.5rem;
            font-size: 1.5rem;
            font-weight: 600;
            text-align: center;
            border: 2px solid var(--border, #e5e7eb);
            border-radius: var(--border-radius, 8px);
            background: var(--bg-secondary, white);
            color: var(--text, #1a1a1a);
            transition: all 0.2s;
          }
          
          .customer-modal__code-input:focus {
            outline: none;
            border-color: var(--primary, #2563eb);
            box-shadow: 0 0 0 3px var(--primary-light, rgba(37, 99, 235, 0.1));
          }
          
          .customer-modal__resend {
            text-align: center;
            margin: 1rem 0;
          }
          
          .customer-modal__resend-timer {
            color: var(--text-muted, #6b7280);
            font-size: 0.875rem;
          }
          
          .customer-modal__loyalty {
            display: flex;
            gap: 1rem;
            margin-top: 0.75rem;
            padding-top: 0.75rem;
            border-top: 1px solid var(--border, #e5e7eb);
          }
          
          .customer-modal__loyalty-tier {
            background: var(--primary, #2563eb);
            color: white;
            padding: 0.25rem 0.75rem;
            border-radius: 20px;
            font-size: 0.75rem;
            font-weight: 600;
            text-transform: uppercase;
          }
          
          .customer-modal__loyalty-points {
            color: var(--text-muted, #6b7280);
            font-size: 0.875rem;
          }
          
          @media (max-width: 400px) {
            .customer-modal__code-input {
              width: 2.5rem;
              height: 3rem;
              font-size: 1.25rem;
            }
          }
        `}</style>
      </div>
    </div>
  );
}

export default CustomerModal;